import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Divider,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Snackbar,
  Stack,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  Avatar
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Save,
  Close,
  Image,
  Link,
  Info,
  Payment,
  Description,
  Public,
  Settings as SettingsIcon,
  UploadFile,
  Percent,
  AttachMoney,
  TextFields
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import pb from './pocketbase';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

// Styled components
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '1rem',
  marginRight: theme.spacing(1),
  minHeight: 64,
  '&.Mui-selected': {
    color: theme.palette.primary.main,
  },
}));

const Section = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const IconHolder = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.light,
  color: theme.palette.primary.main,
  borderRadius: '50%',
  width: 36,
  height: 36,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const Settings = () => {
  // Current date and user
  const currentDateTime = "2025-04-03 04:56:10";
  const currentUser = "MdAbuNafisNiloy";

  // State management
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [appInfo, setAppInfo] = useState(null);
  const [appLinks, setAppLinks] = useState([]);
  const [socialLinks, setSocialLinks] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [scrollingTexts, setScrollingTexts] = useState([]);
  
  const [editingAppInfo, setEditingAppInfo] = useState(false);
  const [appInfoForm, setAppInfoForm] = useState({
    appName: '',
    logo: null,
    commission: 0,
    cashout_charge: 0
  });
  const [previewLogo, setPreviewLogo] = useState(null);
  
  const [openLinkDialog, setOpenLinkDialog] = useState(false);
  const [linkForm, setLinkForm] = useState({ title: '', url: '' });
  const [linkDialogMode, setLinkDialogMode] = useState('add'); // 'add' or 'edit'
  const [currentLinkId, setCurrentLinkId] = useState(null);
  
  const [openSocialDialog, setOpenSocialDialog] = useState(false);
  const [socialForm, setSocialForm] = useState({ name: '', url: '', icon: null });
  const [socialDialogMode, setSocialDialogMode] = useState('add');
  const [currentSocialId, setCurrentSocialId] = useState(null);
  const [previewIcon, setPreviewIcon] = useState(null);
  
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ name: '', number: '', code: '' });
  const [paymentDialogMode, setPaymentDialogMode] = useState('add');
  const [currentPaymentId, setCurrentPaymentId] = useState(null);
  
  const [openPolicyDialog, setOpenPolicyDialog] = useState(false);
  const [policyForm, setPolicyForm] = useState({ title: '', details: '' });
  const [policyDialogMode, setPolicyDialogMode] = useState('add');
  const [currentPolicyId, setCurrentPolicyId] = useState(null);
  
  const [openScrollingTextDialog, setOpenScrollingTextDialog] = useState(false);
  const [scrollingTextForm, setScrollingTextForm] = useState({ text: '' });
  const [scrollingTextDialogMode, setScrollingTextDialogMode] = useState('add');
  const [currentScrollingTextId, setCurrentScrollingTextId] = useState(null);
  
  const [confirmDeleteDialog, setConfirmDeleteDialog] = useState(false);
  const [deleteInfo, setDeleteInfo] = useState({ id: null, type: null });
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch all settings data
  const fetchSettings = async () => {
    try {
      setLoading(true);
      
      // Fetch app info
      const appInfoResponse = await pb.collection('appinfo').getList(1, 1);
      if (appInfoResponse.items.length > 0) {
        setAppInfo(appInfoResponse.items[0]);
        setAppInfoForm({
          appName: appInfoResponse.items[0].appName,
          commission: appInfoResponse.items[0].commission,
          cashout_charge: appInfoResponse.items[0].cashout_charge
        });
        
        if (appInfoResponse.items[0].logo) {
          setPreviewLogo(`${pb.baseUrl}/api/files/${appInfoResponse.items[0].collectionId}/${appInfoResponse.items[0].id}/${appInfoResponse.items[0].logo}`);
        }
      }
      
      // Fetch app links
      const appLinksResponse = await pb.collection('app_links').getList(1, 100);
      setAppLinks(appLinksResponse.items);
      
      // Fetch social links
      const socialLinksResponse = await pb.collection('social_links').getList(1, 100);
      setSocialLinks(socialLinksResponse.items);
      
      // Fetch payment methods
      const paymentMethodsResponse = await pb.collection('payment_methods').getList(1, 100);
      setPaymentMethods(paymentMethodsResponse.items);
      
      // Fetch policies
      const policiesResponse = await pb.collection('policies').getList(1, 100);
      setPolicies(policiesResponse.items);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching settings:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load settings',
        severity: 'error'
      });
      setLoading(false);
    }
  };

  // Load data when component mounts
  useEffect(() => {
    fetchSettings();
    fetchScrollingTexts();
  }, []);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // App Info Methods
  const handleAppInfoChange = (e) => {
    const { name, value } = e.target;
    setAppInfoForm(prev => ({
      ...prev,
      [name]: name === 'commission' || name === 'cashout_charge' ? parseFloat(value) : value
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAppInfoForm(prev => ({
        ...prev,
        logo: file
      }));
      
      // Create preview
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewLogo(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const saveAppInfo = async () => {
    try {
      setIsSubmitting(true);
      
      // Validate form
      if (!appInfoForm.appName.trim()) {
        setSnackbar({
          open: true,
          message: 'App name is required',
          severity: 'error'
        });
        setIsSubmitting(false);
        return;
      }
      
      // Create FormData
      const formData = new FormData();
      formData.append('appName', appInfoForm.appName.trim());
      formData.append('commission', appInfoForm.commission);
      formData.append('cashout_charge', appInfoForm.cashout_charge);
      
      if (appInfoForm.logo instanceof File) {
        formData.append('logo', appInfoForm.logo);
      }
      
      if (appInfo) {
        // Update existing record
        await pb.collection('appinfo').update(appInfo.id, formData);
      } else {
        // Create new record
        await pb.collection('appinfo').create(formData);
      }
      
      setSnackbar({
        open: true,
        message: 'App information saved successfully',
        severity: 'success'
      });
      
      fetchSettings();
      setEditingAppInfo(false);
      setIsSubmitting(false);
    } catch (error) {
      console.error('Error saving app info:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save app information',
        severity: 'error'
      });
      setIsSubmitting(false);
    }
  };

  // App Links Methods
  const openAddLinkDialog = () => {
    setLinkForm({ title: '', url: '' });
    setLinkDialogMode('add');
    setOpenLinkDialog(true);
  };

  const openEditLinkDialog = (link) => {
    setLinkForm({ title: link.title, url: link.url });
    setCurrentLinkId(link.id);
    setLinkDialogMode('edit');
    setOpenLinkDialog(true);
  };

  const handleLinkFormChange = (e) => {
    const { name, value } = e.target;
    setLinkForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const saveLink = async () => {
    try {
      setIsSubmitting(true);
      
      // Validate form
      if (!linkForm.title.trim() || !linkForm.url.trim()) {
        setSnackbar({
          open: true,
          message: 'Title and URL are required',
          severity: 'error'
        });
        setIsSubmitting(false);
        return;
      }
      
      // Validate URL
      try {
        new URL(linkForm.url);
      } catch (e) {
        setSnackbar({
          open: true,
          message: 'Please enter a valid URL',
          severity: 'error'
        });
        setIsSubmitting(false);
        return;
      }
      
      if (linkDialogMode === 'add') {
        // Create new link
        await pb.collection('app_links').create({
          title: linkForm.title.trim(),
          url: linkForm.url.trim()
        });
      } else {
        // Update existing link
        await pb.collection('app_links').update(currentLinkId, {
          title: linkForm.title.trim(),
          url: linkForm.url.trim()
        });
      }
      
      setSnackbar({
        open: true,
        message: `Link ${linkDialogMode === 'add' ? 'added' : 'updated'} successfully`,
        severity: 'success'
      });
      
      fetchSettings();
      setOpenLinkDialog(false);
      setIsSubmitting(false);
    } catch (error) {
      console.error('Error saving link:', error);
      setSnackbar({
        open: true,
        message: `Failed to ${linkDialogMode === 'add' ? 'add' : 'update'} link`,
        severity: 'error'
      });
      setIsSubmitting(false);
    }
  };

  // Social Links Methods
  const openAddSocialDialog = () => {
    setSocialForm({ name: '', url: '', icon: null });
    setPreviewIcon(null);
    setSocialDialogMode('add');
    setOpenSocialDialog(true);
  };

  const openEditSocialDialog = (social) => {
    setSocialForm({ name: social.name, url: social.url });
    setCurrentSocialId(social.id);
    setSocialDialogMode('edit');
    
    if (social.icon) {
      setPreviewIcon(`${pb.baseUrl}/api/files/${social.collectionId}/${social.id}/${social.icon}`);
    } else {
      setPreviewIcon(null);
    }
    
    setOpenSocialDialog(true);
  };

  const handleSocialFormChange = (e) => {
    const { name, value } = e.target;
    setSocialForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleIconChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSocialForm(prev => ({
        ...prev,
        icon: file
      }));
      
      // Create preview
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewIcon(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const saveSocialLink = async () => {
    try {
      setIsSubmitting(true);
      
      // Validate form
      if (!socialForm.name.trim() || !socialForm.url.trim()) {
        setSnackbar({
          open: true,
          message: 'Name and URL are required',
          severity: 'error'
        });
        setIsSubmitting(false);
        return;
      }
      
      // Validate URL
      try {
        new URL(socialForm.url);
      } catch (e) {
        setSnackbar({
          open: true,
          message: 'Please enter a valid URL',
          severity: 'error'
        });
        setIsSubmitting(false);
        return;
      }
      
      // Create FormData
      const formData = new FormData();
      formData.append('name', socialForm.name.trim());
      formData.append('url', socialForm.url.trim());
      
      if (socialForm.icon instanceof File) {
        formData.append('icon', socialForm.icon);
      }
      
      if (socialDialogMode === 'add') {
        // Create new social link
        await pb.collection('social_links').create(formData);
      } else {
        // Update existing social link
        await pb.collection('social_links').update(currentSocialId, formData);
      }
      
      setSnackbar({
        open: true,
        message: `Social link ${socialDialogMode === 'add' ? 'added' : 'updated'} successfully`,
        severity: 'success'
      });
      
      fetchSettings();
      setOpenSocialDialog(false);
      setIsSubmitting(false);
    } catch (error) {
      console.error('Error saving social link:', error);
      setSnackbar({
        open: true,
        message: `Failed to ${socialDialogMode === 'add' ? 'add' : 'update'} social link`,
        severity: 'error'
      });
      setIsSubmitting(false);
    }
  };

  // Payment Methods
  const openAddPaymentDialog = () => {
    setPaymentForm({ name: '', number: '', code: '' });
    setPaymentDialogMode('add');
    setOpenPaymentDialog(true);
  };

  const openEditPaymentDialog = (payment) => {
    setPaymentForm({ 
      name: payment.name, 
      number: payment.number, 
      code: payment.code 
    });
    setCurrentPaymentId(payment.id);
    setPaymentDialogMode('edit');
    setOpenPaymentDialog(true);
  };

  const handlePaymentFormChange = (e) => {
    const { name, value } = e.target;
    setPaymentForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const savePaymentMethod = async () => {
    try {
      setIsSubmitting(true);
      
      // Validate form
      if (!paymentForm.name.trim() || !paymentForm.number.trim()) {
        setSnackbar({
          open: true,
          message: 'Name and Number are required',
          severity: 'error'
        });
        setIsSubmitting(false);
        return;
      }
      
      if (paymentDialogMode === 'add') {
        // Create new payment method
        await pb.collection('payment_methods').create({
          name: paymentForm.name.trim(),
          number: paymentForm.number.trim(),
          code: paymentForm.code.trim()
        });
      } else {
        // Update existing payment method
        await pb.collection('payment_methods').update(currentPaymentId, {
          name: paymentForm.name.trim(),
          number: paymentForm.number.trim(),
          code: paymentForm.code.trim()
        });
      }
      
      setSnackbar({
        open: true,
        message: `Payment method ${paymentDialogMode === 'add' ? 'added' : 'updated'} successfully`,
        severity: 'success'
      });
      
      fetchSettings();
      setOpenPaymentDialog(false);
      setIsSubmitting(false);
    } catch (error) {
      console.error('Error saving payment method:', error);
      setSnackbar({
        open: true,
        message: `Failed to ${paymentDialogMode === 'add' ? 'add' : 'update'} payment method`,
        severity: 'error'
      });
      setIsSubmitting(false);
    }
  };

  // Policies Methods
  const openAddPolicyDialog = () => {
    setPolicyForm({ title: '', details: '' });
    setPolicyDialogMode('add');
    setOpenPolicyDialog(true);
  };

  const openEditPolicyDialog = (policy) => {
    setPolicyForm({ title: policy.title, details: policy.details });
    setCurrentPolicyId(policy.id);
    setPolicyDialogMode('edit');
    setOpenPolicyDialog(true);
  };

  const handlePolicyFormChange = (e) => {
    const { name, value } = e.target;
    setPolicyForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const savePolicy = async () => {
    try {
      setIsSubmitting(true);
      
      // Validate form
      if (!policyForm.title.trim() || !policyForm.details.trim()) {
        setSnackbar({
          open: true,
          message: 'Title and details are required',
          severity: 'error'
        });
        setIsSubmitting(false);
        return;
      }
      
      if (policyDialogMode === 'add') {
        // Create new policy
        await pb.collection('policies').create({
          title: policyForm.title.trim(),
          details: policyForm.details.trim()
        });
      } else {
        // Update existing policy
        await pb.collection('policies').update(currentPolicyId, {
          title: policyForm.title.trim(),
          details: policyForm.details.trim()
        });
      }
      
      setSnackbar({
        open: true,
        message: `Policy ${policyDialogMode === 'add' ? 'added' : 'updated'} successfully`,
        severity: 'success'
      });
      
      fetchSettings();
      setOpenPolicyDialog(false);
      setIsSubmitting(false);
    } catch (error) {
      console.error('Error saving policy:', error);
      setSnackbar({
        open: true,
        message: `Failed to ${policyDialogMode === 'add' ? 'add' : 'update'} policy`,
        severity: 'error'
      });
      setIsSubmitting(false);
    }
  };

  // Scrolling Text Methods
  const fetchScrollingTexts = async () => {
    try {
      const scrollingTextsResponse = await pb.collection('scrolling_text').getList(1, 100);
      setScrollingTexts(scrollingTextsResponse.items);
    } catch (error) {
      console.error('Error fetching scrolling texts:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load scrolling texts',
        severity: 'error'
      });
    }
  };
  
  const openAddScrollingTextDialog = () => {
    setScrollingTextForm({ text: '' });
    setScrollingTextDialogMode('add');
    setOpenScrollingTextDialog(true);
  };

  const openEditScrollingTextDialog = (text) => {
    setScrollingTextForm({ text: text.text });
    setCurrentScrollingTextId(text.id);
    setScrollingTextDialogMode('edit');
    setOpenScrollingTextDialog(true);
  };

  const handleScrollingTextFormChange = (e) => {
    const { name, value } = e.target;
    setScrollingTextForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const saveScrollingText = async () => {
    try {
      setIsSubmitting(true);
      
      // Validate form
      if (!scrollingTextForm.text.trim()) {
        setSnackbar({
          open: true,
          message: 'Text is required',
          severity: 'error'
        });
        setIsSubmitting(false);
        return;
      }
      
      if (scrollingTextDialogMode === 'add') {
        // Create new scrolling text
        await pb.collection('scrolling_text').create({
          text: scrollingTextForm.text.trim()
        });
      } else {
        // Update existing scrolling text
        await pb.collection('scrolling_text').update(currentScrollingTextId, {
          text: scrollingTextForm.text.trim()
        });
      }
      
      setSnackbar({
        open: true,
        message: `Scrolling text ${scrollingTextDialogMode === 'add' ? 'added' : 'updated'} successfully`,
        severity: 'success'
      });
      
      fetchScrollingTexts();
      setOpenScrollingTextDialog(false);
      setIsSubmitting(false);
    } catch (error) {
      console.error('Error saving scrolling text:', error);
      setSnackbar({
        open: true,
        message: `Failed to ${scrollingTextDialogMode === 'add' ? 'add' : 'update'} scrolling text`,
        severity: 'error'
      });
      setIsSubmitting(false);
    }
  };

  // Delete Methods
  const openDeleteDialog = (id, type) => {
    setDeleteInfo({ id, type });
    setConfirmDeleteDialog(true);
  };

  const handleDelete = async () => {
    try {
      setIsSubmitting(true);
      
      switch (deleteInfo.type) {
        case 'app_link':
          await pb.collection('app_links').delete(deleteInfo.id);
          break;
        case 'social_link':
          await pb.collection('social_links').delete(deleteInfo.id);
          break;
        case 'payment_method':
          await pb.collection('payment_methods').delete(deleteInfo.id);
          break;
        case 'policy':
          await pb.collection('policies').delete(deleteInfo.id);
          break;
        case 'scrolling_text':
          await pb.collection('scrolling_text').delete(deleteInfo.id);
          break;
        default:
          throw new Error('Invalid delete type');
      }
      
      setSnackbar({
        open: true,
        message: 'Item deleted successfully',
        severity: 'success'
      });
      
      fetchSettings();
      setConfirmDeleteDialog(false);
      setIsSubmitting(false);
    } catch (error) {
      console.error('Error deleting item:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete item',
        severity: 'error'
      });
      setIsSubmitting(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Settings
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your app settings, links, payment methods, and policies
        </Typography>
      </Box>

      {/* Settings Navigation Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <StyledTab icon={<Info fontSize="small" />} iconPosition="start" label="App Info" />
          <StyledTab icon={<Link fontSize="small" />} iconPosition="start" label="App Links" />
          <StyledTab icon={<Public fontSize="small" />} iconPosition="start" label="Social Links" />
          <StyledTab icon={<Payment fontSize="small" />} iconPosition="start" label="Payment Methods" />
          <StyledTab icon={<Description fontSize="small" />} iconPosition="start" label="Policies" />
          <StyledTab icon={<TextFields fontSize="small" />} iconPosition="start" label="Scrolling Texts" />
        </Tabs>
      </Paper>

      {/* Loading State */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* App Info Tab */}
          {tabValue === 0 && (
            <Section>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <SectionTitle variant="h5">
                  <IconHolder>
                    <Info fontSize="small" />
                  </IconHolder>
                  App Information
                </SectionTitle>
                {editingAppInfo ? (
                  <Box>
                    <Button 
                      variant="outlined" 
                      color="error" 
                      onClick={() => setEditingAppInfo(false)}
                      disabled={isSubmitting}
                      sx={{ mr: 1 }}
                    >
                      Cancel
                    </Button>
                    <Button 
                      variant="contained" 
                      onClick={saveAppInfo}
                      disabled={isSubmitting}
                      startIcon={isSubmitting ? <CircularProgress size={20} /> : <Save />}
                    >
                      Save
                    </Button>
                  </Box>
                ) : (
                  <Button 
                    variant="outlined" 
                    startIcon={<Edit />} 
                    onClick={() => setEditingAppInfo(true)}
                  >
                    Edit
                  </Button>
                )}
              </Box>
              
              {/* App Info View / Edit Mode */}
              <Box>
                {editingAppInfo ? (
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="App Name"
                        name="appName"
                        value={appInfoForm.appName}
                        onChange={handleAppInfoChange}
                        required
                        error={!appInfoForm.appName.trim()}
                        helperText={!appInfoForm.appName.trim() ? "App name is required" : ""}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Commission (%)"
                        name="commission"
                        type="number"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Percent fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                        value={appInfoForm.commission}
                        onChange={handleAppInfoChange}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Cashout Charge"
                        name="cashout_charge"
                        type="number"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AttachMoney />
                            </InputAdornment>
                          ),
                        }}
                        value={appInfoForm.cashout_charge}
                        onChange={handleAppInfoChange}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" gutterBottom>
                        App Logo
                      </Typography>
                      
                      <Box sx={{ mb: 2 }}>
                        <Button
                          component="label"
                          variant="outlined"
                          startIcon={<UploadFile />}
                        >
                          {previewLogo ? 'Change Logo' : 'Upload Logo'}
                          <VisuallyHiddenInput 
                            type="file" 
                            accept="image/*"
                            onChange={handleLogoChange}
                          />
                        </Button>
                        
                        {previewLogo && (
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() => {
                              setPreviewLogo(null);
                              setAppInfoForm(prev => ({ ...prev, logo: null }));
                            }}
                            sx={{ ml: 2 }}
                          >
                            Remove
                          </Button>
                        )}
                      </Box>
                      
                      {previewLogo && (
                        <Box sx={{ mt: 2 }}>
                          <img
                            src={previewLogo}
                            alt="App Logo"
                            style={{ maxWidth: 200, maxHeight: 100, objectFit: 'contain' }}
                          />
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                ) : (
                  <Card variant="outlined">
                    <CardContent>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                          {appInfo?.logo && (
                            <CardMedia
                              component="img"
                              image={`${pb.baseUrl}/api/files/${appInfo.collectionId}/${appInfo.id}/${appInfo.logo}`}
                              alt={appInfo.appName}
                              sx={{ 
                                height: 100, 
                                objectFit: 'contain', 
                                background: '#f5f5f5',
                                borderRadius: 1,
                                mb: 2
                              }}
                            />
                          )}
                          
                          <Typography variant="h6" gutterBottom>
                            {appInfo?.appName || 'App Name Not Set'}
                          </Typography>
                          
                          <Typography variant="body2" color="text.secondary">
                            Last Updated: {formatDate(appInfo?.updated || new Date())}
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={12} md={8}>
                          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                            Settings
                          </Typography>
                          
                          <Box sx={{ display: 'flex', mb: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ width: 200 }}>
                              Commission Rate:
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {appInfo?.commission || 0}%
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ width: 200 }}>
                              Cashout Charge:
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                              ${appInfo?.cashout_charge || 0}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                )}
              </Box>
            </Section>
          )}

          {/* App Links Tab */}
          {tabValue === 1 && (
            <Section>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <SectionTitle variant="h5">
                  <IconHolder>
                    <Link fontSize="small" />
                  </IconHolder>
                  App Links
                </SectionTitle>
                <Button 
                  variant="contained" 
                  startIcon={<Add />}
                  onClick={openAddLinkDialog}
                >
                  Add Link
                </Button>
              </Box>
              
              {appLinks.length === 0 ? (
                <Alert severity="info" sx={{ mb: 3 }}>
                  No app links found. Add your first link using the button above.
                </Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell width="30%">Title</TableCell>
                        <TableCell width="50%">URL</TableCell>
                        <TableCell width="20%" align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {appLinks.map((link) => (
                        <TableRow key={link.id} hover>
                          <TableCell>{link.title}</TableCell>
                          <TableCell>
                            <Link 
                              href={link.url} 
                              target="_blank"
                              rel="noopener noreferrer"
                              color="primary"
                              underline="hover"
                            >
                              {link.url}
                            </Link>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Edit">
                              <IconButton 
                                color="primary"
                                onClick={() => openEditLinkDialog(link)}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton 
                                color="error"
                                onClick={() => openDeleteDialog(link.id, 'app_link')}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Section>
          )}

          {/* Social Links Tab */}
          {tabValue === 2 && (
            <Section>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <SectionTitle variant="h5">
                  <IconHolder>
                    <Public fontSize="small" />
                  </IconHolder>
                  Social Links
                </SectionTitle>
                <Button 
                  variant="contained" 
                  startIcon={<Add />}
                  onClick={openAddSocialDialog}
                >
                  Add Social Link
                </Button>
              </Box>
              
              {socialLinks.length === 0 ? (
                <Alert severity="info" sx={{ mb: 3 }}>
                  No social links found. Add your first social link using the button above.
                </Alert>
              ) : (
                <Grid container spacing={2}>
                  {socialLinks.map((social) => (
                    <Grid item xs={12} md={6} lg={4} key={social.id}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            {social.icon ? (
                              <Avatar 
                                src={`${pb.baseUrl}/api/files/${social.collectionId}/${social.id}/${social.icon}`}
                                alt={social.name}
                                sx={{ mr: 2 }}
                              />
                            ) : (
                              <Avatar sx={{ mr: 2 }}>
                                <Public />
                              </Avatar>
                            )}
                            <Typography variant="h6">
                              {social.name}
                            </Typography>
                          </Box>
                          
                          <Typography 
                            variant="body2" 
                            color="primary"
                            component="a"
                            href={social.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ 
                              display: 'block', 
                              textDecoration: 'none',
                              mb: 2,
                              '&:hover': {
                                textDecoration: 'underline'
                              }
                            }}
                          >
                            {social.url}
                          </Typography>
                          
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button
                              size="small"
                              startIcon={<Edit />}
                              onClick={() => openEditSocialDialog(social)}
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              startIcon={<Delete />}
                              onClick={() => openDeleteDialog(social.id, 'social_link')}
                            >
                              Delete
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Section>
          )}

          {/* Payment Methods Tab */}
          {tabValue === 3 && (
            <Section>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <SectionTitle variant="h5">
                  <IconHolder>
                    <Payment fontSize="small" />
                  </IconHolder>
                  Payment Methods
                </SectionTitle>
                <Button 
                  variant="contained" 
                  startIcon={<Add />}
                  onClick={openAddPaymentDialog}
                >
                  Add Payment Method
                </Button>
              </Box>
              
              {paymentMethods.length === 0 ? (
                <Alert severity="info" sx={{ mb: 3 }}>
                  No payment methods found. Add your first payment method using the button above.
                </Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Number</TableCell>
                        <TableCell>Code</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paymentMethods.map((payment) => (
                        <TableRow key={payment.id} hover>
                          <TableCell>
                            <Typography variant="body1" fontWeight={500}>
                              {payment.name}
                            </Typography>
                          </TableCell>
                          <TableCell>{payment.number}</TableCell>
                          <TableCell>{payment.code}</TableCell>
                          <TableCell align="right">
                            <Tooltip title="Edit">
                              <IconButton 
                                color="primary"
                                onClick={() => openEditPaymentDialog(payment)}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton 
                                color="error"
                                onClick={() => openDeleteDialog(payment.id, 'payment_method')}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Section>
          )}

          {/* Policies Tab */}
          {tabValue === 4 && (
            <Section>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <SectionTitle variant="h5">
                  <IconHolder>
                    <Description fontSize="small" />
                  </IconHolder>
                  Policies
                </SectionTitle>
                <Button 
                  variant="contained" 
                  startIcon={<Add />}
                  onClick={openAddPolicyDialog}
                >
                  Add Policy
                </Button>
              </Box>
              
              {policies.length === 0 ? (
                <Alert severity="info" sx={{ mb: 3 }}>
                  No policies found. Add your first policy using the button above.
                </Alert>
              ) : (
                <Grid container spacing={2}>
                  {policies.map((policy) => (
                    <Grid item xs={12} md={6} key={policy.id}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            {policy.title}
                          </Typography>
                          
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              mb: 2
                            }}
                          >
                            {policy.details}
                          </Typography>
                          
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button
                              size="small"
                              startIcon={<Edit />}
                              onClick={() => openEditPolicyDialog(policy)}
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              startIcon={<Delete />}
                              onClick={() => openDeleteDialog(policy.id, 'policy')}
                            >
                              Delete
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Section>
          )}

          {/* Scrolling Texts Tab */}
          {tabValue === 5 && (
            <Section>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <SectionTitle variant="h5">
                  <IconHolder>
                    <TextFields fontSize="small" />
                  </IconHolder>
                  Scrolling Text Announcements
                </SectionTitle>
                <Button 
                  variant="contained" 
                  startIcon={<Add />}
                  onClick={openAddScrollingTextDialog}
                >
                  Add Scrolling Text
                </Button>
              </Box>
              
              {scrollingTexts.length === 0 ? (
                <Alert severity="info" sx={{ mb: 3 }}>
                  No scrolling texts found. Add your first announcement using the button above.
                </Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Text</TableCell>
                        <TableCell width="20%">Last Updated</TableCell>
                        <TableCell width="15%" align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {scrollingTexts.map((text) => (
                        <TableRow key={text.id} hover>
                          <TableCell>
                            <Typography variant="body1">
                              {text.text}
                            </Typography>
                          </TableCell>
                          <TableCell>{formatDate(text.updated)}</TableCell>
                          <TableCell align="right">
                            <Tooltip title="Edit">
                              <IconButton 
                                color="primary"
                                onClick={() => openEditScrollingTextDialog(text)}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton 
                                color="error"
                                onClick={() => openDeleteDialog(text.id, 'scrolling_text')}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Section>
          )}
        </>
      )}

      {/* App Link Dialog */}
      <Dialog 
        open={openLinkDialog} 
        onClose={() => !isSubmitting && setOpenLinkDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {linkDialogMode === 'add' ? 'Add App Link' : 'Edit App Link'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Title"
              name="title"
              value={linkForm.title}
              onChange={handleLinkFormChange}
              margin="normal"
              required
              error={!linkForm.title.trim()}
              helperText={!linkForm.title.trim() ? "Title is required" : ""}
            />
            
            <TextField
              fullWidth
              label="URL"
              name="url"
              value={linkForm.url}
              onChange={handleLinkFormChange}
              margin="normal"
              required
              error={!linkForm.url.trim()}
              helperText={!linkForm.url.trim() ? "URL is required" : ""}
              placeholder="https://example.com"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenLinkDialog(false)} 
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={saveLink}
            variant="contained" 
            disabled={isSubmitting || !linkForm.title.trim() || !linkForm.url.trim()}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : <Save />}
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Social Link Dialog */}
      <Dialog 
        open={openSocialDialog} 
        onClose={() => !isSubmitting && setOpenSocialDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {socialDialogMode === 'add' ? 'Add Social Link' : 'Edit Social Link'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={socialForm.name}
              onChange={handleSocialFormChange}
              margin="normal"
              required
              error={!socialForm.name.trim()}
              helperText={!socialForm.name.trim() ? "Name is required" : ""}
            />
            
            <TextField
              fullWidth
              label="URL"
              name="url"
              value={socialForm.url}
              onChange={handleSocialFormChange}
              margin="normal"
              required
              error={!socialForm.url.trim()}
              helperText={!socialForm.url.trim() ? "URL is required" : ""}
              placeholder="https://example.com"
            />
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Icon
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<UploadFile />}
                >
                  {previewIcon ? 'Change Icon' : 'Upload Icon'}
                  <VisuallyHiddenInput 
                    type="file" 
                    accept="image/*"
                    onChange={handleIconChange}
                  />
                </Button>
                
                {previewIcon && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => {
                      setPreviewIcon(null);
                      setSocialForm(prev => ({ ...prev, icon: null }));
                    }}
                  >
                    Remove
                  </Button>
                )}
              </Box>
              
              {previewIcon && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <img
                    src={previewIcon}
                    alt="Icon Preview"
                    style={{ width: 64, height: 64, objectFit: 'contain' }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenSocialDialog(false)} 
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={saveSocialLink}
            variant="contained" 
            disabled={isSubmitting || !socialForm.name.trim() || !socialForm.url.trim()}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : <Save />}
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Method Dialog */}
      <Dialog 
        open={openPaymentDialog} 
        onClose={() => !isSubmitting && setOpenPaymentDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {paymentDialogMode === 'add' ? 'Add Payment Method' : 'Edit Payment Method'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={paymentForm.name}
              onChange={handlePaymentFormChange}
              margin="normal"
              required
              error={!paymentForm.name.trim()}
              helperText={!paymentForm.name.trim() ? "Name is required" : ""}
              placeholder="e.g. Paypal, Bank Transfer"
            />
            
            <TextField
              fullWidth
              label="Number"
              name="number"
              value={paymentForm.number}
              onChange={handlePaymentFormChange}
              margin="normal"
              required
              error={!paymentForm.number.trim()}
              helperText={!paymentForm.number.trim() ? "Number is required" : ""}
              placeholder="Account number or payment address"
            />
            
            <TextField
              fullWidth
              label="Code (Optional)"
              name="code"
              value={paymentForm.code}
              onChange={handlePaymentFormChange}
              margin="normal"
              placeholder="Additional code or reference"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenPaymentDialog(false)} 
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={savePaymentMethod}
            variant="contained" 
            disabled={isSubmitting || !paymentForm.name.trim() || !paymentForm.number.trim()}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : <Save />}
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Policy Dialog */}
      <Dialog 
        open={openPolicyDialog} 
        onClose={() => !isSubmitting && setOpenPolicyDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {policyDialogMode === 'add' ? 'Add Policy' : 'Edit Policy'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Title"
              name="title"
              value={policyForm.title}
              onChange={handlePolicyFormChange}
              margin="normal"
              required
              error={!policyForm.title.trim()}
              helperText={!policyForm.title.trim() ? "Title is required" : ""}
              placeholder="e.g. Privacy Policy, Terms of Service"
            />
            
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
              Policy Details
            </Typography>
            
            <TextField
              fullWidth
              name="details"
              value={policyForm.details}
              onChange={handlePolicyFormChange}
              margin="normal"
              required
              error={!policyForm.details.trim()}
              helperText={!policyForm.details.trim() ? "Details are required" : ""}
              multiline
              rows={10}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenPolicyDialog(false)} 
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={savePolicy}
            variant="contained" 
            disabled={isSubmitting || !policyForm.title.trim() || !policyForm.details.trim()}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : <Save />}
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Scrolling Text Dialog */}
      <Dialog 
        open={openScrollingTextDialog} 
        onClose={() => !isSubmitting && setOpenScrollingTextDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {scrollingTextDialogMode === 'add' ? 'Add Scrolling Text' : 'Edit Scrolling Text'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Announcement Text"
              name="text"
              value={scrollingTextForm.text}
              onChange={handleScrollingTextFormChange}
              margin="normal"
              required
              error={!scrollingTextForm.text.trim()}
              helperText={!scrollingTextForm.text.trim() ? "Text is required" : ""}
              placeholder="Enter your announcement text here"
              multiline
              rows={3}
            />
            <Typography variant="caption" color="text.secondary">
              This text will appear in the scrolling banner on your website.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenScrollingTextDialog(false)} 
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={saveScrollingText}
            variant="contained" 
            disabled={isSubmitting || !scrollingTextForm.text.trim()}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : <Save />}
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmDeleteDialog}
        onClose={() => !isSubmitting && setConfirmDeleteDialog(false)}
      >
        <DialogTitle>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to delete this item? This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmDeleteDialog(false)} 
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDelete}
            variant="contained" 
            color="error"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : <Delete />}
          >
            {isSubmitting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({...snackbar, open: false})}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({...snackbar, open: false})} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;